import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { defaultTheme } from "./themes/DefaultTheme";
import { LexicalToolbarPlugin } from "./plugins/LexicalToolbarPlugin";
import { SetInitialValuePlugin } from "./plugins/SetInitialContentPlugin";
import { $getRoot } from "lexical";
import { ImagePastePlugin } from "./plugins/ImagePastePlugin";
import { LexicalTreeViewPlugin } from "./plugins/LexicalTreeViewPlugin";
import { ImageNode } from "./nodes/ImageNode";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

// Editor configuration with theme, namespace, and custom nodes
const editorConfig = {
  namespace: "LexicalEditorNamespace", // Unique namespace for the editor
  nodes: [ImageNode], // Register custom nodes, such as ImageNode
  onError(error: Error) {
    throw error; // Error boundary for Lexical editor
  },
  theme: defaultTheme, // Custom theme for editor styling
};

// Component to render a placeholder in the editor when it's empty
const Placeholder = ({ children }: { children: ReactNode }) => {
  const [editor] = useLexicalComposerContext();
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  useEffect(() => {
    // Update visibility of the placeholder based on editor content
    const update = () => {
      editor.getEditorState().read(() => {
        const rootText = $getRoot().getTextContent();
        setShowPlaceholder(rootText.trim() === ""); // Show placeholder if the content is empty
      });
    };

    // Register update listener
    const unsubscribe = editor.registerUpdateListener(update);
    update();

    return () => unsubscribe(); // Cleanup listener on component unmount
  }, [editor]);

  return showPlaceholder ? <div className="editor-placeholder">{children}</div> : null;
};

// Main LexicalEditor component with forwarding ref
export const LexicalEditor = forwardRef(
  (
    { initialHtml = "", onChange }: { initialHtml?: string; onChange: (editorState: string) => void },
    ref
  ) => {
    const editorRef = useRef<any>(null);

    // Expose a method to get the editor's current state to the parent component
    useImperativeHandle(ref, () => ({
      getEditorState: () => {
        let editorContent = "";
        editorRef.current?.getEditorState().read(() => {
          editorContent = $getRoot().getTextContent();
        });
        return editorContent;
      },
    }));

    // Handle content change and pass updated content to the parent
    const handleChange = (editorState: any) => {
      editorState.read(() => {
        const content = $getRoot().getTextContent(); // Extract text content from the editor
        onChange(content); // Trigger parent onChange callback with updated content
      });
    };

    return (
      <LexicalComposer initialConfig={{ ...editorConfig, editorState: null }}>
        <div className="editor-container">
          {/* Toolbar for editor controls */}
          <LexicalToolbarPlugin />
          <div className="editor-inner">
            {/* RichTextPlugin renders the main content editor */}
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<Placeholder>Enter your reply...</Placeholder>} // Custom placeholder component
              ErrorBoundary={LexicalErrorBoundary} // Error boundary for editor errors
            />
            {/* Plugin for undo/redo functionality */}
            <HistoryPlugin />
            {/* Plugin for auto-focusing the editor */}
            <AutoFocusPlugin />
            {/* Plugin for handling image paste functionality */}
            <ImagePastePlugin />
            {/* Tree view for visualizing editor state (debugging/development) */}
            <LexicalTreeViewPlugin />
            {/* Plugin for detecting and responding to content changes */}
            <OnChangePlugin onChange={handleChange} />
            {/* Plugin for setting the initial HTML content */}
            <SetInitialValuePlugin initHtml={initialHtml} />
          </div>
        </div>
      </LexicalComposer>
    );
  }
);
